import { useState, useEffect, useRef, createRef, ReactElement } from 'react'
import { useRouter } from 'next/router'
import ValidatorWrapper, { ValidatorField } from '@coxy/react-validator'

import validatorRules from 'libs/validator-rules'
import * as ga from 'libs/ga'
import { toFixed } from 'utils/formatters'

import { useAppDispatch, useAppSelector } from 'store/hooks'
import { getCurrencies, setActiveCodeFrom } from 'store/reducers/currencies'
import { createLoan, estimateLoanAmount } from 'store/fetchers/loans'
import { ICurrency } from 'store/fetchers/currencies'
import { EExchangeDirections, IExchangeData } from 'store/fetchers/loans/enum'
import { fetchSettings, getSettings } from 'store/reducers/settings'

import { ICoinPageCurrency } from 'config/coin-pages-list'

import { useTranslation } from 'utils/use-translation'

import { getStoredCurrencyByCodeAndNetwork } from 'utils/get-currency'

import { selectedUser } from '../../../store/reducers/users'

import ButtonBlack from '../button/_color/_black'

import TermsBlock from './components/terms-block'
import InputCalculator from './components/input-calculator'

import cn from 'classnames'

import styles from './styles.module.scss'
import { safeSessionStorage } from 'utils/safe-session-storage'
import useLocationSearch from 'libs/location-search'
import { floorBigNumber } from 'utils/floor-number'
import { ICoinPageData } from 'pages/loans/[coin]/assets/page-data/types'

const DEFAULT_FROM = '0.1'
const ESTIMATE_TTL = 10 * 60 * 1000

declare global {
  interface Window {
    Telegram?: {
      WebApp?: any
    }
    TelegramWebviewProxy?: any
  }
}

const buildAprOptions = (response: any, t: (key: string) => string, isDynamicLoansAvailable?: boolean) => {
  const options = [
    {
      id: 'popular',
      title: 'long_term',
      apr: 'APR',
      percent: response?.fixed_apr_unlimited_loan ?? '',
      duration: 'unlimited_time',
      isDynamic: false,
      period: 'unlimited',
      precison: response?.precision,
    },
    {
      id: 'short_term',
      title: 'short_term',
      apr: 'APR',
      percent: response?.fixed_apr_fixed_loan ?? '',
      duration: 30 + ' ' + t('days'),
      isDynamic: false,
      period: '30',
      precison: response?.precision,
    },
    ...(isDynamicLoansAvailable ? [{
      id: 'min_rate',
      title: 'min_rate',
      apr: 'dynamic',
      percent: response?.dynamic_apr_unlimited_loan ?? '',
      duration: 'unlimited_time',
      isDynamic: true,
      period: 'unlimited',
    }] : [])
  ]

  return options.filter((opt) => Number(opt.percent) > 0)
}

export default function Calculator({
  pageCurrency,
  coinPage,
}: {
  pageCurrency?: ICoinPageCurrency
  coinPage?: ICoinPageData
}): ReactElement {
  const { t, locale, replacePlaceholders } = useTranslation()
  const dispatch = useAppDispatch()
  const router = useRouter()
  const user = useAppSelector(selectedUser)
  const query = useLocationSearch()
  const referralId = Array.isArray(query.referral) ? query.referral[0] : query.referral

  const currencies = useAppSelector(getCurrencies)
  const settings = useAppSelector(getSettings)

  const timer = useRef<any>() // cannot set NodeJS.Timeout
  const validator = createRef<ValidatorWrapper>()
  const requestedPairRef = useRef<[ICurrency, ICurrency]>()
  const skipToEstimate = useRef(false)
  const isFirstLoad = useRef(true)
  const hasCachedEstimate = useRef(false)

  const [from, setFrom] = useState<ICurrency>(null)
  const [to, setTo] = useState<ICurrency>(null)
  const [amountTo, setAmountTo] = useState('')
  const [exchangeData, setExchangeData] = useState<IExchangeData>(null)
  const [isValidAmounts, setIsValidAmounts] = useState<boolean>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingFrom, setLoadingFrom] = useState(false)
  const [isLoadingTo, setLoadingTo] = useState(true)
  const [listDepositCurrencies, setListDepositCurrencies] = useState<ICurrency[]>([])
  const [listLoanCurrencies, setListLoanCurrencies] = useState<ICurrency[]>([])
  const [estimateError, setEstimateError] = useState('')
  const [ltvPercent, setLtvPercent] = useState('')
  const [ltvOptions, setLvtOptions] = useState<string[]>()
  const [aprOptions, setAprOptions] = useState(buildAprOptions(null, t))
  const [activeAprOption, setActiveAprOption] = useState('popular')
  const [isTg, setIsTg] = useState(false)
  const [createError, setCreateError] = useState('')

  const [_amountFrom, _setAmountFrom] = useState(DEFAULT_FROM)
  const amountFrom = useRef(_amountFrom)
  const isAprOptionsSet = useRef(false)
  const aprDiscount = referralId ? safeSessionStorage?.getItem('loanCalculator:firstLoanDiscount') : '0'

  const STORAGE_KEYS = {
    fromCurrencyCode: 'loanCalculatorHome:fromCurrencyCode',
    fromCurrencyNetwork: 'loanCalculatorHome:fromCurrencyNetwork',
    toCurrencyCode: 'loanCalculatorHome:toCurrencyCode',
    toCurrencyNetwork: 'loanCalculatorHome:toCurrencyNetwork',
    loanEstimate: 'loanCalculatorHome:estimate',
  }

  const handleChangeAprOption = (id: string) => {
    setActiveAprOption(id)
  }

  const setAmountFrom = (value: string) => {
    _setAmountFrom(value)
    amountFrom.current = value
  }

  const handleSetFrom = (from: ICurrency) => {
    setFrom(from)

    if (!pageCurrency) {
      isFirstLoad.current = false
      sessionStorage.setItem(STORAGE_KEYS.fromCurrencyCode, from?.code)
      sessionStorage.setItem(STORAGE_KEYS.fromCurrencyNetwork, from?.network)
    }
  }

  const handleSetTo = (to: ICurrency) => {
    setTo(to)

    if (!pageCurrency) {
      isFirstLoad.current = false
      sessionStorage.setItem(STORAGE_KEYS.toCurrencyCode, to?.code)
      sessionStorage.setItem(STORAGE_KEYS.toCurrencyNetwork, to?.network)
    }
  }

  useEffect(() => {
    dispatch(fetchSettings())
    clearTimeout(timer.current)

    return () => {
      clearTimeout(timer.current)
    }
  }, [])

  // for visible forms
  useEffect(() => {
    const { isValid } = validator.current.validate()
    if (isValid !== isValidAmounts) setIsValidAmounts(isValid)
  }, [_amountFrom, amountTo])

  const filterCurrenciesList = (exclude: ICurrency) => (item: ICurrency) => {
    return item.code !== exclude?.code && item.network !== exclude?.network
  }

  useEffect(() => {
    const parseCurrencies = async () => {
      if (currencies?.length === 0) {
        return
      }

      skipToEstimate.current = true

      let depositCurrencies: ICurrency[] = []
      let loanCurrencies: ICurrency[] = []
      let foundPageCurrency: ICurrency = null

      for (const currency of currencies) {
        if (currency.is_loan_deposit_enabled) {
          depositCurrencies.push(currency)
        }
        if (currency.is_loan_receive_enabled) {
          loanCurrencies.push(currency)
        }
        if (pageCurrency && !foundPageCurrency) {
          if (
            currency.code.toLowerCase() === pageCurrency.code.toLowerCase() &&
            currency.network.toLowerCase() === pageCurrency.network.toLowerCase()
          ) {
            foundPageCurrency = currency
          }
        }
      }

      depositCurrencies = depositCurrencies.sort((a, b) => {
        if (a.loan_deposit_priority === null) return 1
        if (b.loan_deposit_priority === null) return -1
        return a.loan_deposit_priority - b.loan_deposit_priority
      })

      loanCurrencies = loanCurrencies.sort((a, b) => {
        if (a.loan_receive_priority === null) return 1
        if (b.loan_receive_priority === null) return -1
        return a.loan_receive_priority - b.loan_receive_priority
      })

      let newFrom: ICurrency = getStoredCurrencyByCodeAndNetwork(
        'loanCalculatorHome:fromCurrencyCode',
        'loanCalculatorHome:fromCurrencyNetwork',
        depositCurrencies,
      )
      let newTo: ICurrency = getStoredCurrencyByCodeAndNetwork(
        'loanCalculatorHome:toCurrencyCode',
        'loanCalculatorHome:toCurrencyNetwork',
        loanCurrencies,
      )

      if (foundPageCurrency?.is_loan_deposit_enabled) {
        newFrom = foundPageCurrency
      } else if (foundPageCurrency?.is_loan_receive_enabled) {
        newTo = foundPageCurrency
        newFrom = depositCurrencies.find(filterCurrenciesList(newTo))
      }

      if (!newFrom && !foundPageCurrency) {
        newFrom = depositCurrencies[0]
      }

      if (newFrom?.code.toLowerCase() === 'usdc' && newFrom?.network.toLowerCase() === 'eth') {
        newTo = loanCurrencies.find((item) => item.code.toLowerCase() === 'btc')
      }

      if (!newTo && window.Telegram?.WebApp?.initData && !pageCurrency) {
        const tgToCurrency = depositCurrencies?.find(
          (item) => item.code.toLowerCase() === 'usdt' && item.network.toLowerCase() === 'ton',
        )

        if (tgToCurrency) {
          newTo = tgToCurrency
          setIsTg(true)
        }
      }

      if (!newTo) {
        newTo = loanCurrencies.find(filterCurrenciesList(newFrom))
      }

      setFrom(newFrom)
      setTo(newTo)

      setListLoanCurrencies(loanCurrencies)
      setListDepositCurrencies(
        depositCurrencies.filter((cur) => cur.loan_deposit_max_amount === null || +cur.loan_deposit_max_amount !== 0),
      )
    }

    void parseCurrencies()
  }, [pageCurrency, currencies])

  const estimate = (
    amount: string,
    direction: EExchangeDirections,
    toCurrency: ICurrency,
    fromCurrency: ICurrency,
    forceCache = false,
  ) => async () => {
    requestedPairRef.current = [fromCurrency, toCurrency]
    const now = new Date()
    const aprOption = aprOptions?.find((it) => it?.id === activeAprOption)

    const { response } = await estimateLoanAmount({
      from_code: fromCurrency.code,
      from_network: fromCurrency.network,
      to_code: toCurrency.code,
      to_network: toCurrency.network,
      amount: amount,
      exchange: direction,
      ltv_percent: ltvPercent,
      receive_to: 'internal_wallet',
      expected_close_date:
        aprOption?.id !== 'short_term' ? null : new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000).toISOString(),
      date_from: now.toISOString(),
      is_dynamic: aprOption?.isDynamic?.toString(),
      referral_code: aprDiscount ? referralId : null
    })

    if ('message' in response) {
      setEstimateError(t(response.message))

      if (direction === EExchangeDirections.direct) {
        setAmountTo('')
      } else {
        setAmountFrom('')
      }
    } else {
      setEstimateError('')
      if (direction === EExchangeDirections.direct) {
        setAmountTo(toFixed(response.amount_to, toCurrency?.decimal_places))

        if (
          (amountFrom.current === from?.loan_deposit_default_amount &&
            !pageCurrency &&
            (from?.ltv_options?.at(-1) ?? settings.ltvDefaultOption) === ltvPercent) ||
          forceCache
        ) {
          sessionStorage.setItem(
            STORAGE_KEYS.loanEstimate,
            JSON.stringify({
              ...response,
              from_code: from.code,
              from_network: from.network,
              to_code: to.code,
              to_network: to.network,
              expiresAt: Date.now() + ESTIMATE_TTL,
            }),
          )
        }
      } else {
        setAmountFrom(toFixed(response.amount_from, fromCurrency?.decimal_places))
      }

      const [fromRef, toRef] = requestedPairRef.current
      if (
        fromRef.code === fromCurrency.code &&
        fromRef.network === fromCurrency.network &&
        toRef.code === toCurrency.code &&
        toRef.network === toCurrency.network
      ) {
        setExchangeData(response)
      }      

      const options = buildAprOptions(response, t, user?.is_dynamic_loans_available)      
      setAprOptions(options)

      if (!isAprOptionsSet.current) {
        setActiveAprOption(options?.[0]?.id ?? 'popular')
        isAprOptionsSet.current = true
      }
    }

    if (direction === EExchangeDirections.direct) {
      setLoadingTo(false)
    } else {
      setLoadingFrom(false)
    }
  }

  const prepareEstimate = (amount: string, direction: EExchangeDirections, _to?: ICurrency, _from?: ICurrency) => {
    clearTimeout(timer.current)

    const toCurrency = _to || to
    const fromCurrency = _from || from
    if (!ltvPercent || !toCurrency || !fromCurrency || Number.isNaN(parseFloat(amount))) {
      return
    }

    if (isFirstLoad.current && direction === EExchangeDirections.direct && !pageCurrency) {
      let cached

      try {
        cached = JSON.parse(sessionStorage.getItem(STORAGE_KEYS.loanEstimate) || '{}')
      } catch {
        sessionStorage.removeItem(STORAGE_KEYS.loanEstimate)
        isFirstLoad.current = false
      }

      const now = Date.now()

      if (cached) {
        if (!cached.expiresAt || now > cached.expiresAt) {
          sessionStorage.removeItem(STORAGE_KEYS.loanEstimate)
          cached = null
        } else {
          const isMatchingDefaultAmount =
            parseFloat(amountFrom.current) === parseFloat(fromCurrency.loan_deposit_default_amount)

          const isMatchingLtv = (fromCurrency.ltv_options?.at?.(-1) ?? settings.ltvDefaultOption) === ltvPercent

          const isMatchingPair =
            cached.from_code?.toLowerCase() === fromCurrency.code?.toLowerCase() &&
            cached.from_network?.toLowerCase() === fromCurrency.network?.toLowerCase() &&
            cached.to_code?.toLowerCase() === toCurrency.code?.toLowerCase() &&
            cached.to_network?.toLowerCase() === toCurrency.network?.toLowerCase()

          const isMatchingInterestPercent =
            cached.interest_percent === aprOptions?.find((it) => it?.id === activeAprOption)?.percent

          if (
            cached?.amount_to &&
            isMatchingDefaultAmount &&
            isMatchingLtv &&
            isMatchingPair &&
            isMatchingInterestPercent
          ) {
            setAmountTo(cached.amount_to)
            setExchangeData(cached)
            setLoadingTo(false)
            const options = buildAprOptions(cached, t, user?.is_dynamic_loans_available)
            setAprOptions(options)

            if (!isAprOptionsSet.current) {
              setActiveAprOption(options?.[0]?.id ?? 'popular')
              isAprOptionsSet.current = true
            }
            hasCachedEstimate.current = true
            return
          }
        }

        isFirstLoad.current = false
      }
    }

    if (direction === EExchangeDirections.direct) {
      setLoadingTo(true)
    } else {
      setLoadingFrom(true)
    }

    timer.current = setTimeout(estimate(amount, direction, toCurrency, fromCurrency), 200)
  }

  useEffect(() => {
    if (!to) {
      return
    }

    if (to.code === from?.code && to.network === from?.network) {
      setFrom(listDepositCurrencies.find(filterCurrenciesList(to)))
      return
    } else if (from?.is_stable && to?.is_stable) {
      setFrom(listDepositCurrencies.find((el) => el.is_stable))
      return
    }

    if (skipToEstimate.current) {
      skipToEstimate.current = false
    } else {
      prepareEstimate(amountFrom.current, EExchangeDirections.direct)
    }
  }, [to])

  useEffect(() => {
    if (!from) {
      return
    }

    isAprOptionsSet.current = false
    let newTo = null
    if (from.code === to?.code && from.network === to?.network) {
      skipToEstimate.current = true
      newTo = listLoanCurrencies.find(filterCurrenciesList(from))
      setTo(newTo)
    } else if (from.is_stable && to?.is_stable) {
      skipToEstimate.current = true
      newTo = listLoanCurrencies.find((el) => el.is_stable)
      setTo(newTo)
    }

    const defaultAmount = from.loan_deposit_default_amount || '10000'
    setAmountFrom(defaultAmount)
    dispatch(setActiveCodeFrom(from.code))
    prepareEstimate(defaultAmount, EExchangeDirections.direct, newTo, from)
  }, [from])

  const handleChangeLtvPercent = (value: string) => {
    const numeric = parseFloat(value.replace('%', '')) / 100
    setLtvPercent(String(numeric))
  }

  useEffect(() => {
    setLoadingTo(true)

    const newLtvOptions = (from?.ltv_options ?? settings?.ltvOptions ?? []).map((it) => `${+it * 100}%`)
    const activeTab = from?.ltv_options?.[from?.ltv_options?.length - 1] ?? settings?.ltvDefaultOption ?? '0'

    setLvtOptions(newLtvOptions)
    setLtvPercent(activeTab)
    prepareEstimate(amountFrom.current, EExchangeDirections.direct)
  }, [from, settings?.ltvDefaultOption])

  useEffect(() => {
    setLoadingTo(true)
    prepareEstimate(amountFrom.current, EExchangeDirections.direct)
  }, [ltvPercent, activeAprOption])

  useEffect(() => {
    if (pageCurrency) return undefined

    let cancelled = false

    const poll = async () => {
      if (!from || !to || !ltvPercent) return

      const raw = sessionStorage.getItem(STORAGE_KEYS.loanEstimate)

      const isMatchingLtv = (from?.ltv_options?.at(-1) ?? settings.ltvDefaultOption) === ltvPercent
      const isMatchingAmount =
        parseFloat(amountFrom.current || '') === parseFloat(from.loan_deposit_default_amount || '')

      if (!isMatchingLtv || !isMatchingAmount) return scheduleNext()

      try {
        const cached = raw ? JSON.parse(raw) : null
        const now = Date.now()

        if (!cached?.expiresAt || now > cached.expiresAt) {
          sessionStorage.removeItem(STORAGE_KEYS.loanEstimate)
          hasCachedEstimate.current = false
          setLoadingTo(true)
          await estimate(amountFrom.current, EExchangeDirections.direct, to, from, true)()
        }
      } catch {
        sessionStorage.removeItem(STORAGE_KEYS.loanEstimate)
        hasCachedEstimate.current = false
        setLoadingTo(true)
        await estimate(amountFrom.current, EExchangeDirections.direct, to, from, true)()
      }

      scheduleNext()
    }

    const scheduleNext = () => {
      if (!cancelled) {
        setTimeout(poll, 60000)
      }
    }

    poll()

    return () => {
      cancelled = true
    }
  }, [from, to, ltvPercent])

  async function handleCreateLoan() {
    const { isValid } = validator.current.validate()

    if (!isValid) {
      return
    }

    setIsLoading(true)

    const aprOption = aprOptions?.find((it) => it?.id === activeAprOption)

    const loan = await createLoan({
      user_id: user?.id,
      deposit: {
        currency_code: from.code,
        currency_network: from.network,
        expected_amount: exchangeData.amount_from,
        receive_from: 'external_wallet',
      },
      loan: {
        currency_code: to.code,
        currency_network: to.network,
      },
      ltv_percent: ltvPercent,
      expected_close_date:
        aprOption?.id !== 'short_term' ? null : new Date(new Date().getTime() + 30 * 24 * 60 * 60 * 1000).toISOString(),
      is_dynamic: aprOption?.isDynamic,
    })

    if (typeof loan === 'string' && loan?.toLowerCase()?.includes('dynamic loan')) {

      setCreateError(loan?.includes('unavailable') 
        ? t('dynamic_loan_unavailable')
        : locale === 'en' ? loan : t('dynamic_loan_amount_exceeds_limit')
      )
      setIsLoading(false)
      setTimeout(() => setCreateError(''), 10000)
    }

    if (!loan || (typeof loan !== 'string' && !loan.loan_id)) {
      setIsLoading(false)
      return
    }

    ga.event({
      action: 'Loan_landing_step_1_get',
    })

    if (typeof loan !== 'string') {
      await router.push({
        pathname: '/loan/confirm',
        query: {
          from_code: from.code,
          from_network: from.network,
          to_code: to.code,
          to_network: to.network,
          amount: exchangeData.amount_from,
          loan_id: loan.loan_id,
          ltv_percent: ltvPercent,
          isExternal: 'true',
          duration: aprOption?.period,
          is_dynamic: aprOption?.isDynamic
        },
      })
    }
  }

  const handleSetAmountFrom = (amount: string) => {
    if (!pageCurrency) isFirstLoad.current = false

    clearTimeout(timer.current)
    setAmountFrom(amount)
    prepareEstimate(amount, EExchangeDirections.direct)
  }

  const handleSetAmountTo = (amount: string) => {
    if (!pageCurrency) isFirstLoad.current = false

    clearTimeout(timer.current)
    setAmountTo(amount)
    prepareEstimate(amount, EExchangeDirections.reverse)
  }

  const borrowTitleCodes = ['btc', 'floki', 'jasmy', 'volt', 'bone', 'quack', 'babydoge']
  const calculatorTitle =
    borrowTitleCodes.includes(pageCurrency?.code) || pageCurrency?.borrowTitle
      ? `${t('borrow_against_bone')} ${(pageCurrency?.customFields?.calculatorHeading || pageCurrency.name)
          ?.split(' ')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ')}`
      : `${
          locale !== 'pt'
            ? (pageCurrency?.customFields?.calculatorHeading || pageCurrency?.name)
                ?.split(' ')
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ') + ' '
            : ''
        }${t('loan_calculator')}`

  return (
    <div className={styles.container}>
      <ValidatorWrapper ref={validator}>
        <div className={styles.content}>
          {pageCurrency && (
            <h2 className={styles.title}>
              {coinPage?.calculatorTitle ? t(coinPage?.calculatorTitle) : calculatorTitle}
            </h2>
          )}
          <div className={styles.inputs}>
            <ValidatorField value={_amountFrom} rules={validatorRules.depositAmount(from, t, replacePlaceholders)}>
              {({ isValid, message }) => (
                <InputCalculator
                  className={styles['input-from']}
                  onEnter={handleCreateLoan}
                  coin={from}
                  isLoading={isLoadingFrom}
                  onChangeAmount={handleSetAmountFrom}
                  onChangeCoin={handleSetFrom}
                  value={_amountFrom}
                  label={t('collateral_capital')}
                  list={listDepositCurrencies}
                  inputWrapperClassName={styles.wrapper}
                  listFullWidth
                  inputMode="decimal"
                  type={'secondary'}
                >
                  {(estimateError ||
                    (!isValid && !isLoadingFrom && (amountTo !== '—' || amountFrom.current !== '—'))) && (
                    <div className={styles['error-amount']}>{estimateError || message}</div>
                  )}
                </InputCalculator>
              )}
            </ValidatorField>

            <ValidatorField value={amountTo} rules={validatorRules.loanAmount(to, t, replacePlaceholders)}>
              {({ isValid, message }) => (
                <InputCalculator
                  className={styles['input-to']}
                  onEnter={handleCreateLoan}
                  coin={to}
                  isLoading={isLoadingTo}
                  onChangeAmount={handleSetAmountTo}
                  onChangeCoin={handleSetTo}
                  value={amountTo}
                  label={t('loan')}
                  list={listLoanCurrencies}
                  inputWrapperClassName={styles.wrapper}
                  isStableFirst={true}
                  listFullWidth
                  inputMode="decimal"
                  type={'secondary'}
                  showList={!isTg}
                >
                  {!estimateError && !isValid && !isLoadingTo && (amountTo !== '—' || amountFrom.current !== '—') && (
                    <div className={styles['error-amount']}>{message}</div>
                  )}
                  {!estimateError && isValid && activeAprOption === 'min_rate' && !isLoadingTo &&
                  <>
                    {+exchangeData?.dynamic_loans_max_limit_usd <= 0
                      ? <span className={cn(styles['error-amount'])}>{t('dynamic_loan_unavailable')}</span>
                      : <span className={cn(styles['error-amount'],
                         {[styles.amount__info]: +exchangeData?.dynamic_loans_max_limit_usd >= +amountTo}
                      )}>{
                        replacePlaceholders(t('limit_for_dynamic_loans'), {
                          amount: floorBigNumber(exchangeData?.dynamic_loans_max_limit_usd, 6)
                        })
                      }</span>
                    }
                  </>
                }
                </InputCalculator>
              )}
            </ValidatorField>
          </div>
        </div>

        <TermsBlock
          exchange={exchangeData}
          to={to}
          ltvOptions={ltvOptions}
          handleChangeLvtPercent={handleChangeLtvPercent}
          ltvPercent={+ltvPercent * 100 + '%'}
          handleChangeAprOption={handleChangeAprOption}
          aprOptions={aprOptions}
          activeAprOption={activeAprOption}
          aprDiscount={aprDiscount}
        />

        <ButtonBlack
          isLoading={isLoading}
          disabled={!isValidAmounts
            || +exchangeData?.dynamic_loans_max_limit_usd <= +amountTo && activeAprOption === 'min_rate'
          }
          onClick={handleCreateLoan}
          className={styles['button__get-loan']}
        >
          {t('get_loan')}

          {createError && <span className={styles.confirm__error}>{createError}</span>}
        </ButtonBlack>
      </ValidatorWrapper>
    </div>
  )
}